import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Shirt, Upload, PackageCheck, Building2, Gem, Wand2, LayoutGrid } from "lucide-react";
import { homeTrack } from "@/lib/homeTrack";

const BRAND = "#8C52FF";
const INK = "#0A0616";

/* ───────────────────────── 1 · HERO (statico, primo schermo) ─────────────────────────
   iter347 · nuovo impianto: H1 con «Abbigliamento personalizzato» in viola
   Squiddi, tre azioni con funzioni diverse (prova il personalizzatore qui in
   home · catalogo · designer) e, a destra, le foto che si alternano.
   Via il selettore «Cosa vuoi creare?», la riga «Vai diretto a» e il link
   «Tutto il catalogo». */
const HERO_SHOTS = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/2e2711ac-b16f-44c4-9ff9-3fcc1778dd62/images/5d9f9711b80c463b3c4fc4848c5b145a1c1b6bb1e571e48c3c125482f7aeff96.jpeg",
    alt: "T-shirt con stampa, felpa con ricamo e cappellino personalizzati",
    caption: "T-shirt, felpe e cappellini",
    eager: true,
  },
  {
    src: "https://images.unsplash.com/photo-1588332380978-65750b30b7b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    alt: "Due t-shirt con la stessa stampa sulla schiena",
    caption: "Stampa per gruppi e squadre",
  },
  {
    src: "https://images.unsplash.com/photo-1653032352741-47204a1d33e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    alt: "Logo ricamato in bianco su tessuto scuro",
    caption: "Ricamo sul capo",
  },
  {
    src: "https://images.unsplash.com/photo-1722181879579-b97695e360c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
    alt: "Team con grembiuli coordinati",
    caption: "Divise per la tua attività",
  },
];

const HeroShots = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % HERO_SHOTS.length), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative" data-testid="home-hero-visual">
      <div className="relative rounded-[28px] overflow-hidden border bg-[#F1EDFF]" style={{ borderColor: "rgba(39,24,78,0.12)" }}>
        <div className="relative h-[320px] md:h-[470px]">
          {HERO_SHOTS.map((s, idx) => (
            <img key={s.src} src={s.src} alt={s.alt}
              loading={s.eager ? "eager" : "lazy"} fetchPriority={s.eager ? "high" : undefined} decoding="async"
              data-testid={`home-hero-shot-${idx}`}
              className="absolute inset-0 w-full h-full object-cover object-center transition-[opacity,transform] duration-[1200ms] ease-out"
              style={{ opacity: idx === i ? 1 : 0, transform: idx === i ? "scale(1)" : "scale(1.05)" }} />
          ))}
        </div>

        <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3.5 py-2 text-[11.5px] font-black shadow-sm"
          style={{ color: INK }} data-testid="home-hero-shot-caption">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND }} />
          {HERO_SHOTS[i].caption}
        </span>

        <div className="absolute left-4 right-4 bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/95 backdrop-blur border px-4 py-3"
          style={{ borderColor: "rgba(39,24,78,0.1)" }}>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] font-bold" style={{ color: "rgba(39,24,78,0.75)" }}>
            <span className="inline-flex items-center gap-1.5"><Check size={13} style={{ color: BRAND }} /> Colori e taglie</span>
            <span className="inline-flex items-center gap-1.5"><Check size={13} style={{ color: BRAND }} /> Stampa o ricamo</span>
            <span className="inline-flex items-center gap-1.5"><Check size={13} style={{ color: BRAND }} /> Anteprima sul capo</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="home-hero-dots">
            {HERO_SHOTS.map((s, idx) => (
              <button key={s.src} type="button" onClick={() => setI(idx)} aria-label={`Foto ${idx + 1}: ${s.caption}`}
                aria-current={idx === i} data-testid={`home-hero-dot-${idx}`}
                className="h-2 rounded-full transition-all duration-300"
                style={{ width: idx === i ? 22 : 8, background: idx === i ? BRAND : "rgba(39,24,78,0.22)" }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const HERO_ACTIONS = [
  {
    id: "try", Ic: Wand2, label: "Provalo adesso", sub: "Il personalizzatore, qui nella home",
    testid: "home-hero-cta-primary",
  },
  {
    id: "catalog", Ic: LayoutGrid, label: "Sfoglia i capi", sub: "T-shirt, felpe, polo, divise, cappellini",
    to: "/shop", testid: "home-hero-cta-secondary",
  },
  {
    id: "wecreate", Ic: Building2, label: "Mi aiuta un designer", sub: "Gratis · progetti da 25 capi",
    to: "/squiddi-designer", testid: "home-hero-cta-wecreate",
  },
];

export const HeroHome = () => {
  const goToCustomizer = () => {
    homeTrack("home_hero_cta_click", { cta: "try", to: "#home-configurator" });
    document.querySelector('[data-testid="home-configurator"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative bg-white pt-8 pb-12 md:pt-14 md:pb-20 overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ backgroundImage: "radial-gradient(60% 50% at 12% 0%, rgba(140,82,255,0.12), transparent 70%)" }} />
      <div className="relative container-x grid lg:grid-cols-[1.08fr_0.92fr] gap-10 lg:gap-14 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[10.5px] font-black uppercase tracking-[0.16em]"
            style={{ borderColor: "rgba(140,82,255,0.3)", color: BRAND, background: "#F7F4FF" }} data-testid="home-hero-eyebrow">
            Stampa · Ricamo · Patch
          </span>
          <h1 className="mt-4 font-display font-black tracking-tight leading-[1.02]" style={{ color: INK, fontSize: "clamp(2.1rem,6.4vw,4rem)" }}
            data-testid="home-hero-title">
            <span style={{ color: BRAND }}>Abbigliamento personalizzato</span> per te, il tuo gruppo o la tua attività.
          </h1>
          <p className="mt-4 md:mt-5 text-[15px] md:text-lg leading-relaxed max-w-xl" style={{ color: "rgba(39,24,78,0.72)" }}
            data-testid="home-hero-subtitle">
            Crea t-shirt, felpe e divise con stampa o ricamo. Hai già la grafica? Personalizza online.
            Ti serve una mano? Con Squiddi Designer un designer ti guida gratis in tutto il progetto.
          </p>

          <div className="mt-7 grid gap-2.5 sm:grid-cols-3" data-testid="home-hero-actions">
            {HERO_ACTIONS.map(({ id, Ic, label, sub, to, testid }) => {
              const first = id === "try";
              const body = (
                <>
                  <span className={`grid place-items-center w-10 h-10 rounded-xl ${first ? "bg-white/20 text-white" : ""}`}
                    style={first ? undefined : { background: "#F1EDFF", color: BRAND }}>
                    <Ic size={19} />
                  </span>
                  <span className="block mt-3 font-display font-black text-[15px] leading-tight">{label}</span>
                  <span className={`block mt-1 text-[12px] leading-snug ${first ? "text-white/80" : ""}`}
                    style={first ? undefined : { color: "rgba(39,24,78,0.6)" }}>{sub}</span>
                  <span className={`mt-3 inline-flex items-center gap-1.5 text-[12px] font-black ${first ? "text-white" : ""}`}
                    style={first ? undefined : { color: BRAND }}>
                    {first ? "Prova il personalizzatore" : "Apri"}
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </>
              );
              const shared = "group rounded-[20px] p-4 text-left transition-all duration-300 hover:-translate-y-0.5";
              return first ? (
                <button key={id} type="button" onClick={goToCustomizer} data-testid={testid}
                  className={`${shared} text-white shadow-[0_18px_40px_-20px_rgba(140,82,255,0.9)] hover:shadow-[0_26px_55px_-22px_rgba(140,82,255,0.95)]`}
                  style={{ background: "linear-gradient(135deg,#8C52FF 0%,#6C3DEE 100%)" }}>
                  {body}
                </button>
              ) : (
                <Link key={id} to={to} data-testid={testid}
                  onClick={() => homeTrack("home_hero_cta_click", { cta: id, to })}
                  className={`${shared} bg-white border hover:border-[#8C52FF] hover:shadow-[0_18px_40px_-26px_rgba(39,24,78,0.8)]`}
                  style={{ borderColor: "rgba(39,24,78,0.14)", color: INK }}>
                  {body}
                </Link>
              );
            })}
          </div>

          <Link to="/shop?macro=aziendale" data-testid="home-hero-b2b-link"
            onClick={() => homeTrack("home_hero_cta_click", { cta: "b2b", to: "/shop?macro=aziendale" })}
            className="mt-5 inline-flex items-center gap-2 text-[13px] font-black transition-colors" style={{ color: "rgba(39,24,78,0.65)" }}>
            Divise e merchandising per la tua attività
            <ArrowUpRight size={15} style={{ color: BRAND }} />
          </Link>
        </div>

        <HeroShots />
      </div>
    </section>
  );
};

/* ───────────────────────── 3 · COME FUNZIONA (3 passi) ───────────────────────── */
const STEPS = [
  { n: "01", Ic: Shirt, t: "Scegli il capo", d: "T-shirt, felpe, polo, divise da lavoro, cappellini e accessori: parti dal catalogo." },
  { n: "02", Ic: Upload, t: "Personalizzalo", d: "Carica il logo, scegli colori, taglie e lavorazione: vedi anteprima e prezzo stimato mentre configuri." },
  { n: "03", Ic: PackageCheck, t: "Conferma e segui l'ordine", d: "Confermi, produciamo e segui l'ordine fino alla consegna." },
];

export const HowItWorks = () => (
  <section className="bg-white py-16 md:py-24" data-testid="home-how">
    <div className="container-x">
      <h2 className="font-display font-black tracking-tight leading-[1.05]" style={{ color: INK, fontSize: "clamp(1.8rem,4.4vw,3rem)" }}>
        Dal capo alla tua idea, in tre passi.
      </h2>
      <div className="mt-9 grid gap-4 md:grid-cols-3">
        {STEPS.map(({ n, Ic, t, d }) => (
          <div key={n} className="rounded-[22px] border p-6 bg-white" style={{ borderColor: "rgba(39,24,78,0.12)" }} data-testid={`home-step-${n}`}>
            <div className="flex items-center justify-between">
              <span className="grid place-items-center w-11 h-11 rounded-full" style={{ background: "#F1EDFF", color: BRAND }}><Ic size={20} /></span>
              <span className="font-display font-black text-3xl" style={{ color: "rgba(39,24,78,0.12)" }}>{n}</span>
            </div>
            <h3 className="font-display font-black text-lg md:text-xl mt-4" style={{ color: INK }}>{t}</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: "rgba(39,24,78,0.7)" }}>{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-7 flex flex-wrap gap-3">
        <Link to="/shop" data-testid="home-how-catalog" onClick={() => homeTrack("home_how_click", { to: "/shop" })}
          className="inline-flex items-center gap-2 rounded-full text-white font-black text-[14px] px-6 min-h-[48px]" style={{ background: INK }}>
          Vai al catalogo <ArrowRight size={16} />
        </Link>
        <Link to="/squiddi-designer" data-testid="home-how-wecreate" onClick={() => homeTrack("home_how_click", { to: "/squiddi-designer" })}
          className="inline-flex items-center gap-2 rounded-full border-2 font-black text-[14px] px-6 min-h-[48px]"
          style={{ borderColor: "rgba(39,24,78,0.2)", color: INK }}>
          Come funziona Squiddi Designer
        </Link>
      </div>
    </div>
  </section>
);

/* ───────────────────────── 4 · CATEGORIE + SETTORI + BASIC/PREMIUM ───────────────────────── */
const CATS = [
  { label: "T-shirt", to: "/shop?cat=t-shirt", img: "https://images.unsplash.com/photo-1612287665393-dc4ce7714f88?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Felpe", to: "/shop?cat=felpe", img: "https://images.unsplash.com/photo-1564557287817-3785e38ec1f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Polo", to: "/shop?cat=polo", img: "https://images.unsplash.com/photo-1586363104862-3a5e2ab60d99?auto=format&fit=crop&w=900&q=80" },
  { label: "Divise da lavoro", to: "/shop?cat=lavoro", img: "https://images.unsplash.com/photo-1742840255166-b576896fa0a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Cappellini e accessori", to: "/shop?cat=accessori", img: "https://images.unsplash.com/photo-1720534490358-bc2ad29d51d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
  { label: "Tutto il catalogo", to: "/shop", img: "https://images.unsplash.com/photo-1632194978058-4f2f48bc68c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" },
];

const SECTORS = [
  { label: "Promozionale", to: "/shop?macro=promozionale", dot: "#8C52FF" },
  { label: "Ristorazione", to: "/shop?macro=ristorazione", dot: "#F5821F" },
  { label: "Aziendale", to: "/shop?macro=aziendale", dot: "#0A0616" },
  { label: "Urban", to: "/shop?macro=urban", dot: "#6C3DEE" },
  { label: "Sanitario", to: "/shop?macro=sanitario", dot: "#14B8A6" },
  { label: "Accessori", to: "/shop?macro=accessori", dot: "#D99B08" },
];

const SPECS = [
  ["Grammatura", "150 g/m²", "220 g/m²"],
  ["Cotone", "100% pettinato", "100% ring-spun"],
  ["Resistenza lavaggi", "40°C", "60°C · anti-shrink"],
  ["Finiture", "Standard", "Cuciture rinforzate"],
  ["Elasticità", "Buona", "4-way stretch"],
];

export const Categories = () => {
  const [specsOpen, setSpecsOpen] = useState(false);
  return (
    <section className="py-16 md:py-24" style={{ background: "#F7F6FE" }} data-testid="home-categories">
      <div className="container-x">
        <h2 className="font-display font-black tracking-tight leading-[1.05]" style={{ color: INK, fontSize: "clamp(1.8rem,4.4vw,3rem)" }}>
          Da cosa vuoi partire?
        </h2>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="home-categories-grid">
          {CATS.map((c) => (
            <Link key={c.label} to={c.to} data-testid={`home-cat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => homeTrack("home_category_click", { label: c.label, to: c.to })}
              className="group relative rounded-[20px] overflow-hidden border bg-white min-h-[150px] md:min-h-[210px] flex items-end"
              style={{ borderColor: "rgba(39,24,78,0.12)" }}>
              <img src={c.img} alt={c.label} loading="lazy" decoding="async"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
              <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,6,22,0) 28%, rgba(10,6,22,0.86) 100%)" }} />
              <span className="relative p-4 font-display font-black text-white text-[15px] md:text-lg leading-tight">{c.label}</span>
            </Link>
          ))}
        </div>

        <div className="mt-9">
          <span className="block text-[10.5px] font-black uppercase tracking-[0.18em]" style={{ color: "rgba(39,24,78,0.5)" }}>
            Cerchi per settore?
          </span>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="home-sectors">
            {SECTORS.map((s) => (
              <Link key={s.label} to={s.to} data-testid={`home-sector-${s.label.toLowerCase()}`}
                onClick={() => homeTrack("home_sector_click", { label: s.label, to: s.to })}
                className="group inline-flex items-center gap-2 min-h-[44px] rounded-full bg-white border px-4 text-[13px] font-bold transition-all duration-200 hover:-translate-y-0.5 hover:border-[#8C52FF] hover:shadow-[0_12px_26px_-18px_rgba(39,24,78,0.8)]"
                style={{ borderColor: "rgba(39,24,78,0.12)", color: "rgba(10,6,22,0.82)" }}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0 transition-transform group-hover:scale-125" style={{ background: s.dot }} />
                {s.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-9 rounded-[24px] bg-white border overflow-hidden shadow-[0_24px_60px_-40px_rgba(39,24,78,0.65)]"
          style={{ borderColor: "rgba(39,24,78,0.1)" }} data-testid="home-quality">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 sm:p-6">
            <span className="grid place-items-center w-12 h-12 rounded-2xl shrink-0 text-white shadow-[0_12px_26px_-14px_rgba(140,82,255,0.9)]"
              style={{ background: "linear-gradient(135deg,#8C52FF 0%,#6C3DEE 100%)" }}>
              <Gem size={20} />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-black text-[16px] md:text-lg leading-tight" style={{ color: INK }}>
                Due livelli di qualità: Basic e Premium
              </h3>
              <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "rgba(39,24,78,0.6)" }}>
                Grammatura, cotone e finiture: guarda in cosa cambiano prima di scegliere.
              </p>
            </div>
            <button type="button" onClick={() => { setSpecsOpen((v) => !v); homeTrack("home_quality_toggle", { open: !specsOpen }); }}
              aria-expanded={specsOpen} data-testid="home-quality-toggle"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#27184E]/18 text-[#0A0616] px-6 min-h-[48px] text-[13.5px] font-black shrink-0 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#0A0616] hover:text-white hover:border-[#0A0616]">
              {specsOpen ? "Chiudi il confronto" : "Confronta"}
              <ArrowUpRight size={15} className="opacity-60" />
            </button>
          </div>
          {specsOpen && (
            <div className="px-5 sm:px-6 pb-6 pt-1 animate-fade-up" data-testid="home-quality-table">
              <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(39,24,78,0.1)" }}>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-5 px-4 py-3" style={{ background: "#F7F5FF" }}>
                  <span className="text-[10.5px] font-black uppercase tracking-[0.12em]" style={{ color: "rgba(39,24,78,0.45)" }}>Caratteristica</span>
                  <span className="text-right text-[10.5px] font-black uppercase tracking-[0.12em]" style={{ color: "#7A55E8" }}>Basic</span>
                  <span className="text-right text-[10.5px] font-black uppercase tracking-[0.12em]" style={{ color: BRAND }}>Premium</span>
                </div>
                {SPECS.map(([k, b, p], i) => (
                  <div key={k} className="grid grid-cols-[1fr_auto_auto] gap-x-5 px-4 py-3 text-[12.5px] border-t"
                    style={{ borderColor: "rgba(39,24,78,0.07)", background: i % 2 ? "#FCFBFF" : "#fff" }}>
                    <span style={{ color: "rgba(39,24,78,0.62)" }}>{k}</span>
                    <span className="text-right font-bold" style={{ color: INK }}>{b}</span>
                    <span className="text-right font-bold" style={{ color: BRAND }}>{p}</span>
                  </div>
                ))}
              </div>
              <Link to="/shop" data-testid="home-quality-shop" className="mt-4 inline-flex items-center gap-2 text-[13px] font-black" style={{ color: BRAND }}>
                Vedi i capi disponibili <ArrowRight size={15} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
